import React from "react";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { WarningRounded } from "@mui/icons-material";

interface LocationCardDraftProgressProps {
  /** Completion percentage 0–100. */
  completionPercent: number;
  /** Comma-separated list of missing required fields, if any. */
  missingFields?: string;
}

const ProgressWrap = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 3,
  marginTop: 2,
});

const BarLabel = styled(Box, {
  shouldForwardProp: (prop) => prop !== "ownerCritical",
})<{ ownerCritical: boolean }>(({ ownerCritical }) => ({
  fontSize: "0.7857rem",  // 11px at 14px root
  color: ownerCritical ? "#d74747" : "#5E7782",
  fontWeight: 600,
  display: "flex",
  alignItems: "center",
  gap: 4,
  flexWrap: "wrap",
}));

const ProgressBg = styled(Box)({
  background: "#e9edf0",
  borderRadius: 6,
  height: 6,
  width: 200,
  overflow: "hidden",
});

const ProgressFill = styled(Box, {
  shouldForwardProp: (prop) => prop !== "ownerPercent" && prop !== "ownerCritical",
})<{ ownerPercent: number; ownerCritical: boolean }>(({ ownerPercent, ownerCritical }) => ({
  height: "100%",
  borderRadius: 6,
  width: `${ownerPercent}%`,
  background: ownerCritical ? "#d74747" : "#3f4fc1",
}));

/**
 * Profile completion progress bar shown on Draft location cards.
 * Bar color shifts to red when `completionPercent` is below 50% (critical state).
 */
const LocationCardDraftProgress = ({ completionPercent, missingFields }: LocationCardDraftProgressProps) => {
  const isCritical = completionPercent < 50;
  return (
    <ProgressWrap aria-label={`Profile completion: ${completionPercent}%`}>
      <BarLabel ownerCritical={isCritical}>
        Profile completion: {completionPercent}%
        {missingFields &&
          <>
            &nbsp;—&nbsp;
            <Box component="span" sx={{ color: isCritical ? "#d74747" : "#c9960a", fontSize: 'inherit' }}>
              <WarningRounded sx={{ fontSize: "0.8571rem", verticalAlign: "text-top" }} aria-hidden="true" />
              {" "}Missing: {missingFields}
            </Box>
          </>
        }
      </BarLabel>
      <ProgressBg>
        <ProgressFill
          ownerPercent={completionPercent}
          ownerCritical={isCritical}
          role="progressbar"
          aria-valuenow={completionPercent}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </ProgressBg>
    </ProgressWrap>
  );
};

export default LocationCardDraftProgress;

